import { AxiosRequestConfig } from 'axios';

import api from 'services/api';
import type { BaseTerritoryData } from 'services/basesServices';

import { getMockBaseFlags } from 'utils/blockingDevMock';

import { getAccessToken } from './accessTokenServices';

export type BaseFlags = {
  active?: boolean;
  gasReleased?: boolean;
  oracleStatus?: string;
};

export type BaseTerritoryResult =
  | { ok: true; baseCode: string; territory: BaseTerritoryData }
  | { ok: false; baseCode: string };

const territoryUrl = (baseCode: string): string =>
  `industrial/customer/v1/customers/me/bases/${baseCode}/territory`;

export const getFlagsFromTerritory = (
  territory?: BaseTerritoryData
): BaseFlags => ({
  active: territory?.base?.active,
  gasReleased: territory?.base?.gasReleased,
  oracleStatus: territory?.base?.oracleStatus,
});

export const getBaseTerritoryData = async (
  baseCode: string
): Promise<BaseTerritoryResult> => {
  const access_token = await getAccessToken();

  const parameters = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      access_token,
    },
  } as AxiosRequestConfig;

  try {
    const response = await api.get<BaseTerritoryData>(
      territoryUrl(baseCode),
      parameters
    );

    return { ok: true, baseCode, territory: response.data };
  } catch (error) {
    return { ok: false, baseCode };
  }
};

export const getBasesTerritories = async (
  baseCodes: string[]
): Promise<{
  territoryByBase: Record<string, BaseTerritoryData>;
  failed: string[];
}> => {
  const results = await Promise.all(baseCodes.map(getBaseTerritoryData));

  const territoryByBase: Record<string, BaseTerritoryData> = {};
  const failed: string[] = [];

  results.forEach((result) => {
    if (result.ok) {
      territoryByBase[result.baseCode] = result.territory;
    } else {
      failed.push(result.baseCode);
    }
  });

  baseCodes.forEach((baseCode) => {
    const mockedFlags = getMockBaseFlags(baseCode, baseCodes);
    if (!mockedFlags) return;

    const territory = territoryByBase[baseCode] ?? ({} as BaseTerritoryData);
    territoryByBase[baseCode] = {
      ...territory,
      base: { ...territory.base, ...mockedFlags },
    };
  });

  return { territoryByBase, failed };
};

export default getBasesTerritories;
